import React, { useEffect, useState, useCallback, useRef } from 'react';
import { TrendingUp, Sparkles, Loader2, Download, FileDown, DollarSign, Share2, Copy, Trash2, ExternalLink } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from 'recharts';
import jsPDF from 'jspdf';
import { suiteAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Panel, PanelLoader, Card, Stat, Chip, copyText } from './shared';
import { toast } from 'sonner';

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [busy, setBusy] = useState('');
  const [days, setDays] = useState(30);
  const [campaignId, setCampaignId] = useState('');
  const [shares, setShares] = useState([]);
  const reportRef = useRef(null);

  const loadShares = useCallback(() => suiteAPI.listShares().then(r => setShares(r.data)).catch(() => {}), []);
  useEffect(() => { loadShares(); }, [loadShares]);

  const createShare = async () => {
    const selected = data?.campaigns?.find(c => c.id === campaignId);
    const { data: s } = await suiteAPI.createShare({
      scope: campaignId ? 'campaign' : 'overview',
      campaign_id: campaignId || null,
      title: selected ? `${selected.name} — Performance Report` : 'Marketing Performance Report',
      expires_days: 60,
    });
    copyText(`${window.location.origin}/report/${s.token}`);
    toast.success('Share link created and copied to your clipboard');
    loadShares();
  };

  const load = useCallback(() => {
    suiteAPI.analyticsOverview(days).then(r => setData(r.data)).catch(() => toast.error('Could not load analytics'));
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const runInsights = async () => {
    setBusy('ai');
    try {
      const { data: r } = await suiteAPI.aiInsights(campaignId ? { campaign_id: campaignId } : {});
      setInsights(r);
    } catch { toast.error('Insight generation failed'); }
    finally { setBusy(''); }
  };

  const exportPdf = async () => {
    setBusy('pdf');
    try {
      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const t = data.totals;
      doc.setFontSize(20); doc.text('Marketing Performance Report', 40, 50);
      doc.setFontSize(10); doc.setTextColor(120);
      doc.text(`Generated ${new Date().toLocaleString()} · last ${days} days`, 40, 68);
      doc.setTextColor(0); doc.setFontSize(12);
      let y = 105;
      const rows = [
        ['Total spend', `$${t.spend.toLocaleString()}`],
        ['Revenue', `$${t.revenue.toLocaleString()}`],
        ['ROI', `${t.roi}%`],
        ['ROAS', `${t.roas}x`],
        ['Impressions', t.impressions.toLocaleString()],
        ['Clicks & scans', t.total_clicks.toLocaleString()],
        ['QR scans', t.qr_scans.toLocaleString()],
        ['Page views', t.page_views.toLocaleString()],
        ['Leads', t.leads.toLocaleString()],
        ['Conversions', t.conversions.toLocaleString()],
        ['Cost per lead', `$${t.cpl}`],
        ['Cost per click', `$${t.cpc}`],
        ['Emails sent', t.email_sent.toLocaleString()],
        ['Email open rate', `${t.email_open_rate}%`],
      ];
      rows.forEach(([k, v]) => { doc.text(`${k}`, 40, y); doc.text(`${v}`, 320, y); y += 20; });
      y += 14; doc.setFontSize(14); doc.text('Campaign breakdown', 40, y); y += 20; doc.setFontSize(10);
      data.campaigns.forEach(c => {
        if (y > 780) { doc.addPage(); y = 50; }
        doc.text(`${c.name} — ${c.status}`, 40, y);
        doc.text(`spend $${c.spend} · rev $${c.revenue} · leads ${c.leads} · ROI ${c.roi}%`, 240, y);
        y += 16;
      });
      if (insights) {
        doc.addPage(); y = 50; doc.setFontSize(14); doc.text('AI performance review', 40, y); y += 24; doc.setFontSize(10);
        doc.text(doc.splitTextToSize(insights.headline || '', 500), 40, y); y += 30;
        (insights.recommendations || []).forEach(r => {
          if (y > 760) { doc.addPage(); y = 50; }
          doc.text(doc.splitTextToSize(`• [${r.impact}] ${r.action} — ${r.why}`, 500), 40, y);
          y += 34;
        });
      }
      doc.save('mmos-marketing-report.pdf');
    } catch { toast.error('PDF export failed'); }
    finally { setBusy(''); }
  };

  if (!data) return <PanelLoader />;
  const t = data.totals;

  return (
    <Panel title="Analytics & Reporting" subtitle="Spend, ROI, attribution and funnel performance across every channel — with an AI review you can hand to leadership." testId="suite-analytics"
      action={
        <div className="flex gap-2 flex-wrap">
          <a href={suiteAPI.exportCsvUrl('campaigns')} target="_blank" rel="noreferrer"><Button className="bg-zinc-800 text-white"><Download size={14} className="mr-1.5" /> CSV</Button></a>
          <Button onClick={exportPdf} disabled={busy === 'pdf'} className="bg-zinc-800 text-white" data-testid="analytics-pdf">
            {busy === 'pdf' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <FileDown size={14} className="mr-1.5" />} PDF report
          </Button>
          <Button onClick={runInsights} disabled={busy === 'ai'} className="bg-[#b8ff00] text-black font-bold" data-testid="analytics-insights">
            {busy === 'ai' ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />} AI review
          </Button>
        </div>
      }>
      <div className="flex gap-1.5 mb-5 items-center flex-wrap">
        {[7, 30, 90].map(d => <Chip key={d} active={days === d} onClick={() => setDays(d)} testId={`analytics-days-${d}`}>{d} days</Chip>)}
        <Button size="sm" onClick={createShare} className="bg-zinc-800 text-white text-xs ml-2" data-testid="share-create">
          <Share2 size={12} className="mr-1.5" /> Create client share link
        </Button>
      </div>

      {shares.length > 0 && (
        <Card className="p-4 mb-5" data-testid="share-list">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Client share links (read-only, no login needed)</p>
          {shares.map(s => {
            const url = `${window.location.origin}/report/${s.token}`;
            return (
              <div key={s.id} className="flex items-center gap-2 py-1.5 border-b border-zinc-800 last:border-0">
                <p className="text-xs text-white truncate flex-1">{s.title} <span className="text-zinc-500">· {s.views} views · expires {s.expires_at?.slice(0, 10)}</span></p>
                <p className="text-[11px] text-[#b8ff00] font-mono truncate max-w-[280px]">{url}</p>
                <Button size="sm" variant="ghost" className="text-zinc-400" onClick={() => { copyText(url); toast.success('Link copied'); }} data-testid={`share-copy-${s.id}`}><Copy size={12} /></Button>
                <a href={url} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="text-zinc-400"><ExternalLink size={12} /></Button></a>
                <Button size="sm" variant="ghost" className="text-zinc-500 hover:text-red-400" onClick={() => suiteAPI.deleteShare(s.id).then(loadShares)} data-testid={`share-revoke-${s.id}`}><Trash2 size={12} /></Button>
              </div>
            );
          })}
        </Card>
      )}

      <div ref={reportRef} className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
          <Stat label="Spend" value={`$${t.spend.toLocaleString()}`} />
          <Stat label="Revenue" value={`$${t.revenue.toLocaleString()}`} tone="text-[#b8ff00]" />
          <Stat label="ROI" value={`${t.roi}%`} sub={`ROAS ${t.roas}x`} />
          <Stat label="Clicks" value={t.total_clicks.toLocaleString()} sub={`CTR ${t.ctr}%`} />
          <Stat label="Leads" value={t.leads} tone="text-cyan-400" sub={`CPL $${t.cpl}`} />
          <Stat label="Conversions" value={t.conversions} sub={`CPA $${t.cpa}`} />
          <Stat label="Email opens" value={`${t.email_open_rate}%`} sub={`${t.email_sent} sent`} />
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <p className="text-sm font-bold text-white mb-3">Engagement over time</p>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.timeline}>
                  <CartesianGrid stroke="#27272a" vertical={false} />
                  <XAxis dataKey="date" stroke="#52525b" fontSize={10} tickFormatter={v => v.slice(5)} />
                  <YAxis stroke="#52525b" fontSize={10} />
                  <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 12 }} />
                  <Line type="monotone" dataKey="clicks" stroke="#b8ff00" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="scans" stroke="#22d3ee" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="leads" stroke="#a78bfa" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
          <Card className="p-5">
            <p className="text-sm font-bold text-white mb-3 flex items-center gap-1.5"><DollarSign size={13} /> Spend by channel</p>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.channel_mix}>
                  <CartesianGrid stroke="#27272a" vertical={false} />
                  <XAxis dataKey="channel" stroke="#52525b" fontSize={10} />
                  <YAxis stroke="#52525b" fontSize={10} />
                  <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="spend" fill="#b8ff00" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-white">Campaign performance</p>
            <select value={campaignId} onChange={e => setCampaignId(e.target.value)} className="bg-zinc-800 border border-zinc-700 rounded text-[11px] text-zinc-300 px-2 py-1" data-testid="analytics-campaign-select">
              <option value="">All campaigns (AI review scope)</option>
              {data.campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {data.campaigns.length === 0 ? <p className="text-xs text-zinc-500">No campaigns yet.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-zinc-500 text-left">
                  {['Campaign', 'Status', 'Budget', 'Spend', 'Revenue', 'ROI', 'Clicks', 'Leads', 'CPL'].map(h => <th key={h} className="py-2 pr-4 font-medium">{h}</th>)}
                </tr></thead>
                <tbody>
                  {data.campaigns.map(c => (
                    <tr key={c.id} className="border-t border-zinc-800" data-testid={`analytics-row-${c.id}`}>
                      <td className="py-2 pr-4 text-white">{c.name}</td>
                      <td className="py-2 pr-4 text-zinc-400 capitalize">{c.status}</td>
                      <td className="py-2 pr-4 text-zinc-400">${Number(c.budget).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-zinc-300">${c.spend.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-[#b8ff00]">${c.revenue.toLocaleString()}</td>
                      <td className="py-2 pr-4 text-zinc-300">{c.roi}%</td>
                      <td className="py-2 pr-4 text-zinc-300">{c.total_clicks}</td>
                      <td className="py-2 pr-4 text-cyan-400">{c.leads}</td>
                      <td className="py-2 pr-4 text-zinc-400">${c.cpl}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <p className="text-sm font-bold text-white mb-3">Top links</p>
            {data.top_links.length === 0 ? <p className="text-xs text-zinc-500">No links yet.</p> : data.top_links.map(l => (
              <div key={l.id} className="flex justify-between py-1.5 border-b border-zinc-800 last:border-0">
                <span className="text-xs text-zinc-300 truncate max-w-[70%]">{l.title}</span><span className="text-xs text-[#b8ff00]">{l.clicks}</span>
              </div>
            ))}
          </Card>
          <Card className="p-5">
            <p className="text-sm font-bold text-white mb-3">Top QR codes</p>
            {data.top_qr.length === 0 ? <p className="text-xs text-zinc-500">No QR codes yet.</p> : data.top_qr.map(q => (
              <div key={q.id} className="flex justify-between py-1.5 border-b border-zinc-800 last:border-0">
                <span className="text-xs text-zinc-300 truncate max-w-[70%]">{q.name}</span><span className="text-xs text-[#b8ff00]">{q.scans}</span>
              </div>
            ))}
          </Card>
        </div>

        {insights && (
          <Card className="p-5" data-testid="ai-insights">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-[#b8ff00]" />
              <p className="text-sm font-bold text-white">AI performance review</p>
              <span className="text-[11px] text-zinc-500 ml-auto">Health score {insights.health_score}/100</span>
            </div>
            <p className="text-sm text-zinc-200 mb-4">{insights.headline}</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-[#b8ff00] mb-1">Working</p>
                {(insights.whats_working || []).map((x, i) => <p key={i} className="text-xs text-zinc-300 py-0.5">· {x}</p>)}
                <p className="text-[11px] uppercase tracking-wider text-amber-400 mt-3 mb-1">Not working</p>
                {(insights.whats_not || []).map((x, i) => <p key={i} className="text-xs text-zinc-300 py-0.5">· {x}</p>)}
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Recommendations</p>
                {(insights.recommendations || []).map((r, i) => (
                  <div key={i} className="py-1.5 border-b border-zinc-800 last:border-0">
                    <p className="text-xs text-white">{r.action}</p>
                    <p className="text-[11px] text-zinc-500">{r.why}</p>
                    <p className="text-[10px] text-zinc-600">impact {r.impact} · effort {r.effort}</p>
                  </div>
                ))}
              </div>
            </div>
            {insights.budget_advice && <p className="text-xs text-cyan-400 mt-4">Budget: {insights.budget_advice}</p>}
            {(insights.next_7_days || []).length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Next 7 days</p>
                {insights.next_7_days.map((x, i) => <p key={i} className="text-xs text-zinc-300">{i + 1}. {x}</p>)}
              </div>
            )}
          </Card>
        )}
      </div>
    </Panel>
  );
}
